// Served as a classic script, so this file must stay import-free: a shared chunk would emit an
// `import` statement and break the whole bundle.

// Event replay (cf. Angular's `withEventReplay`): main.js runs render-blocking, well before the
// React drawer hydrates, so trigger clicks are captured here and handed over on mount.
(() => {
  const DRAW_TRIGGER_HREF_PATTERN = /[?&]drawNumber=/;
  const PENDING_FALLBACK_MS = 2500;
  const PRIMARY_BUTTON = 0;

  let pendingAnchor: HTMLAnchorElement | null = null;
  let fallbackTimeoutId: number | null = null;

  const clearFallbackTimer = () => {
    if (fallbackTimeoutId !== null) window.clearTimeout(fallbackTimeoutId);

    fallbackTimeoutId = null;
  };

  const onEarlyClick = (event: MouseEvent) => {
    if (event.button !== PRIMARY_BUTTON || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;

    const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;

    if (!anchor || !DRAW_TRIGGER_HREF_PATTERN.test(anchor.getAttribute('href') ?? '')) return;

    // Capturing these would change where the link opens.
    if (anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;

    event.preventDefault();
    clearFallbackTimer();

    pendingAnchor = anchor;
    fallbackTimeoutId = window.setTimeout(() => {
      if (pendingAnchor !== anchor) return;

      pendingAnchor = null;
      window.location.assign(anchor.href);
    }, PENDING_FALLBACK_MS);
  };

  document.addEventListener('click', onEarlyClick, true);

  window.__ytDrawSliderReplay = {
    takePending: () => {
      document.removeEventListener('click', onEarlyClick, true);
      clearFallbackTimer();

      const anchor = pendingAnchor;

      pendingAnchor = null;

      return anchor;
    },
  };
})();

window.setState =
  window.setState ||
  function (key: string, value: string) {
    window.localStorage.setItem(key, value);
    window.dispatchEvent(new StorageEvent('storage', { key, newValue: value }));
  };

Element.prototype.observeResize =
  Element.prototype.observeResize ||
  function (this: Element, callback: () => void) {
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(callback);

      resizeObserver.observe(this);
    } else {
      let lastRect = this.getBoundingClientRect();

      setInterval(() => {
        const rect = this.getBoundingClientRect();

        if (Math.floor(rect.width) != Math.floor(lastRect.width) || Math.floor(rect.height) != Math.floor(lastRect.height)) {
          lastRect = rect;
          callback();
        }
      }, 500);
    }
  };

document.addEventListener('click', (event) => {
  const target = (event.target as HTMLElement)?.closest?.('.js-reload-page');

  if (target) {
    event.preventDefault();
    window.location.reload();
  }
});

HTMLElement.prototype.onOutsideClick =
  HTMLElement.prototype.onOutsideClick ||
  function (this: HTMLElement, handler: (e: MouseEvent | TouchEvent) => void, otherDependenceElement?: HTMLElement[]) {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!event.target || !this || this.contains(event.target as Node)) {
        return;
      }

      if (otherDependenceElement && otherDependenceElement.some((r) => r?.contains(event.target as Node))) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
  };

function replaceWithDiv(element: Element): HTMLDivElement {
  const div = document.createElement('div');

  Array.from(element.attributes).forEach((attr) => div.setAttribute(attr.name, attr.value));

  while (element.firstChild) {
    div.appendChild(element.firstChild);
  }

  element.parentNode?.replaceChild(div, element);

  return div;
}

function isYourtownSite(): boolean {
  return document.documentElement.getAttribute('data-app')?.toLowerCase() === 'yourtown';
}

function suppressEpiFormsGroupingSemantics() {
  document.querySelectorAll('.EPiServerForms fieldset').forEach((fieldset) => {
    const describedBy = fieldset.getAttribute('aria-describedby');

    if (describedBy) {
      fieldset.removeAttribute('aria-describedby');
      fieldset.querySelectorAll('input').forEach((input) => {
        const existing = input.getAttribute('aria-describedby');

        input.setAttribute('aria-describedby', existing ? `${existing} ${describedBy}` : describedBy);
      });
    }

    fieldset.querySelectorAll('legend').forEach((legend) => replaceWithDiv(legend));
    replaceWithDiv(fieldset);
  });
}

function moveFormTitleOutsideForm() {
  document.querySelectorAll('form.EPiServerForms').forEach((form) => {
    const title = form.querySelector(':scope > .Form__Title');

    if (title && form.parentNode) {
      form.parentNode.insertBefore(title, form);
    }
  });
}

function suppressFormLandmarkAnnouncement() {
  document.querySelectorAll<HTMLFormElement>('form.EPiServerForms').forEach((form) => {
    if (form.getAttribute('role') !== 'presentation') {
      form.setAttribute('role', 'presentation');
    }
  });
}

function fixAdjacentInlineLinkReading() {
  document.querySelectorAll<HTMLElement>('.yt-a-rich-text p, .yt-a-rich-text div').forEach((container) => {
    if (container.dataset.ytLinkRowFixed) {
      return;
    }

    const directAnchors = Array.from(container.children).filter((el) => el.tagName === 'A');

    if (directAnchors.length < 2) {
      return;
    }

    const hasOtherContent = Array.from(container.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        return (node as Element).tagName !== 'A';
      }

      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').trim().length > 0;
      }

      return false;
    });

    if (hasOtherContent) {
      return;
    }

    Array.from(container.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.remove();
      }
    });

    container.dataset.ytLinkRowFixed = 'true';
    container.style.display = 'flex';
    container.style.flexWrap = 'wrap';
    container.style.alignItems = 'center';
    container.style.gap = container.style.gap || '0.75rem';
  });
}

function fixMultiLinkRowGrouping() {
  if (!isYourtownSite()) {
    return;
  }

  document.querySelectorAll<HTMLElement>('.yt-a-rich-text p, .yt-a-rich-text div').forEach((container) => {
    if (container.dataset.ytListRowFixed) {
      return;
    }

    const directAnchors = Array.from(container.children).filter((el) => el.tagName === 'A');

    if (directAnchors.length < 3) {
      return;
    }

    const hasOtherContent = Array.from(container.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        return !directAnchors.includes(node as Element);
      }

      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').trim().length > 0;
      }

      return false;
    });

    if (hasOtherContent) {
      return;
    }

    container.setAttribute('role', 'list');

    directAnchors.forEach((anchor) => {
      const item = document.createElement('span');

      item.setAttribute('role', 'listitem');
      anchor.parentNode?.insertBefore(item, anchor);
      item.appendChild(anchor);
    });

    container.dataset.ytListRowFixed = 'true';
  });
}

function fixHeroBannerCtaLinkReading() {
  if (!isYourtownSite()) {
    return;
  }

  document.querySelectorAll<HTMLElement>('.hero-banner-cta, .image-card-buttons-wrapper').forEach((container) => {
    if (container.dataset.ytCtaRowFixed) {
      return;
    }

    const directLinkSpans = Array.from(container.children).filter(
      (el) => el.tagName === 'SPAN' && el.querySelector(':scope > a')
    );

    if (directLinkSpans.length < 2) {
      return;
    }

    const hasOtherContent = Array.from(container.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        return !directLinkSpans.includes(node as Element);
      }

      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').trim().length > 0;
      }

      return false;
    });

    if (hasOtherContent) {
      return;
    }

    Array.from(container.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.remove();
      }
    });

    container.dataset.ytCtaRowFixed = 'true';
  });
}

function fixEmailLinkPronunciation() {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="mailto:"]').forEach((link) => {
    if (link.dataset.ytEmailAriaFixed) {
      return;
    }

    const email = link.href.replace(/^mailto:/i, '').split('?')[0];
    const [localPart, domainPart] = email.split('@');

    if (!localPart || !domainPart) {
      return;
    }

    const spellShortSegments = isYourtownSite();

    const spokenDomain = domainPart
      .split('.')
      .map((segment) => (spellShortSegments && segment.length <= 2 ? segment.split('').join(' ') : segment))
      .join(' dot ');

    link.setAttribute('aria-label', `${localPart} at ${spokenDomain}`);
    link.dataset.ytEmailAriaFixed = 'true';
  });
}

function fixDateInputFormatPronunciation() {
  if (!isYourtownSite()) {
    return;
  }

  document.querySelectorAll<HTMLInputElement>('input[data-f-datainput]').forEach((input) => {
    if (input.dataset.ytDateFormatFixed) {
      return;
    }

    const placeholder = input.placeholder;

    if (!placeholder || !/^[dmy]+([/.-][dmy]+){2}$/i.test(placeholder)) {
      return;
    }

    const spokenFormat = placeholder
      .split('/')
      .map((segment) => segment.split('').join(' '))
      .join(' slash ');

    const hint = document.createElement('span');

    hint.id = `${input.id || Math.random().toString(36).slice(2)}-date-format-hint`;
    hint.className = 'visually-hidden';
    hint.textContent = `Date format ${spokenFormat}`;
    input.insertAdjacentElement('afterend', hint);

    const existingDescribedBy = input.getAttribute('aria-describedby');

    input.setAttribute('aria-describedby', existingDescribedBy ? `${existingDescribedBy} ${hint.id}` : hint.id);
    input.dataset.ytDateFormatFixed = 'true';
  });
}

function fixTextLabelReading() {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
    if (link.dataset.ytTextLabelFixed) {
      return;
    }

    const prev = link.previousSibling;

    if (!prev || prev.nodeType !== Node.TEXT_NODE || !/^\s*text:\s*$/i.test(prev.textContent || '')) {
      return;
    }

    const labelText = (prev.textContent || '').trim();
    const number = link.textContent?.trim() || '';

    const hiddenLabel = document.createElement('span');

    hiddenLabel.setAttribute('aria-hidden', 'true');
    hiddenLabel.textContent = labelText;
    prev.parentNode?.replaceChild(hiddenLabel, prev);

    link.setAttribute('aria-label', `Text ${number}`);
    link.dataset.ytTextLabelFixed = 'true';
  });
}

function fixSamePageAnchorAnnouncement() {
  document.querySelectorAll<HTMLElement>('[data-yt-link-row-fixed] a[href^="#"]').forEach((link) => {
    if (link.dataset.ytSamePageFixed) {
      return;
    }

    const targetId = link.getAttribute('href')?.slice(1);
    const target = targetId ? document.getElementById(targetId) : null;

    if (!targetId || !target) {
      return;
    }

    link.dataset.ytSamePageFixed = 'true';
    link.dataset.ytScrollTarget = targetId;
    link.removeAttribute('href');
    link.setAttribute('role', 'link');
    link.setAttribute('tabindex', '0');

    const activate = (event: Event) => {
      event.preventDefault();

      const scrollTarget = document.getElementById(link.dataset.ytScrollTarget || '');

      if (!scrollTarget) {
        return;
      }

      scrollTarget.scrollIntoView({ behavior: 'instant', block: 'start' });

      const findFocusFallback = (from: Element): HTMLElement | null => {
        let node: Element | null = from;

        while (node) {
          const sibling = node.nextElementSibling;

          if (sibling) {
            return sibling as HTMLElement;
          }

          node = node.parentElement;
        }

        return null;
      };

      const focusTarget =
        scrollTarget.getAttribute('aria-hidden') === 'true'
          ? (scrollTarget.querySelector<HTMLElement>(
              'h1, h2, h3, h4, h5, h6, a, button, input, [tabindex]'
            ) ?? findFocusFallback(scrollTarget))
          : scrollTarget;

      if (!focusTarget || focusTarget.getAttribute('aria-hidden') === 'true') {
        return;
      }

      const hadTabIndex = focusTarget.hasAttribute('tabindex');

      if (!hadTabIndex) {
        focusTarget.setAttribute('tabindex', '-1');
      }

      focusTarget.focus({ preventScroll: true });

      if (!hadTabIndex) {
        focusTarget.addEventListener(
          'blur',
          () => {
            focusTarget.removeAttribute('tabindex');
          },
          { once: true }
        );
      }
    };

    link.addEventListener('click', activate);
    link.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter') {
        activate(event);
      }
    });
  });
}

function runAccessibilityDomFixes() {
  suppressEpiFormsGroupingSemantics();
  moveFormTitleOutsideForm();
  suppressFormLandmarkAnnouncement();
  fixAdjacentInlineLinkReading();
  fixMultiLinkRowGrouping();
  fixHeroBannerCtaLinkReading();
  fixEmailLinkPronunciation();
  fixDateInputFormatPronunciation();
  fixTextLabelReading();
  fixSamePageAnchorAnnouncement();
}

function initAccessibilityDomFixes() {
  runAccessibilityDomFixes();

  const accessibilityDomObserver = new MutationObserver(runAccessibilityDomFixes);

  accessibilityDomObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccessibilityDomFixes);
} else {
  initAccessibilityDomFixes();
}
